import { API_BASE } from "../lib/env";
import { encodeNsPath } from "../lib/namespace";
import { firebaseAuth } from "../lib/firebase";
import { useAuthStore } from "../stores/authStore";
import type { OperationStatus } from "../types/api";

// Client-side request timeout. Longer than the server's 30s ?wait=true block so
// a synchronous upsert isn't aborted prematurely, but bounded so a stalled
// connection can't hang a poll loop forever.
const REQUEST_TIMEOUT_MS = 60000;

function currentUid(): string | null {
  return firebaseAuth().currentUser?.uid ?? null;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  if (!text) return res.statusText;
  try {
    const j = JSON.parse(text);
    if (j && typeof j.error === "string") return j.error;
  } catch {
    // not JSON — fall through to raw text
  }
  return text;
}

// request performs an authenticated fetch against the Vector Forge API. It
// attaches the Firebase ID token, and on a 401 force-refreshes the token once
// and retries; a second 401 signs the user out. All error responses (the
// {"error": ...} envelope, or raw text) throw an ApiError.
export async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  // Pin the user that initiated this request so a delayed retry/sign-out can't
  // act on a DIFFERENT user who signed in meanwhile.
  const startUid = currentUid();
  const token = await useAuthStore.getState().getToken();
  if (!token) throw new ApiError(401, "not authenticated");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
    Authorization: `Bearer ${token}`,
  };
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    signal: init.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  if (res.status === 401 && retry && currentUid() === startUid) {
    // Token may be stale — force refresh and retry exactly once, but only while
    // it's still the same user.
    const fresh = await useAuthStore.getState().getToken(true);
    if (fresh) return request<T>(path, init, false);
  }
  if (res.status === 401) {
    // Only sign out if this request's user is still the current one; never sign
    // out a newer user because an older request's token expired.
    if (startUid !== null && currentUid() === startUid) {
      await useAuthStore.getState().signOutUser();
    }
    throw new ApiError(401, "session expired");
  }
  if (!res.ok) {
    throw new ApiError(res.status, await parseError(res));
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// nsPath builds a per-namespace URL, encoding each segment while keeping the
// {workspaceID}/{name} slash.
export function nsPath(fullNs: string, suffix = ""): string {
  return `/v1/namespaces/${encodeNsPath(fullNs)}${suffix}`;
}

// pollOperation polls an async write's operation until it commits or fails.
// A 404/503 (no status cache) resolves optimistically as "queued".
export async function pollOperation(
  fullNs: string,
  operationId: string,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<OperationStatus> {
  const interval = opts.intervalMs ?? 800;
  const timeout = opts.timeoutMs ?? 30000;
  const deadline = Date.now() + timeout;
  for (;;) {
    let st: OperationStatus;
    try {
      st = await request<OperationStatus>(
        nsPath(fullNs, `/operations/${encodeURIComponent(operationId)}`),
      );
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 503)) {
        return { operation_id: operationId, status: "queued" };
      }
      throw e;
    }
    if (st.status === "committed") return st;
    if (st.status === "failed") throw new ApiError(500, st.error || "operation failed");
    if (Date.now() > deadline) return st;
    await new Promise((r) => setTimeout(r, interval));
  }
}
